'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';

type AuthTab = 'signin' | 'signup';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  backgroundColor: 'var(--bg)',
  border: '1px solid var(--border-med)',
  borderRadius: '5px',
  color: 'var(--ink)',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 300,
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: 'var(--ink-mute)',
  marginBottom: '8px',
};

function ErrorBanner({ message }: { message: string }) {
  if (!message) return null;
  // Friendly error messages
  const friendly = message.includes('rate limit') || message.includes('too many')
    ? 'Too many attempts. Please wait a few minutes before trying again.' : message.includes('Invalid login credentials') || message.includes('invalid_credentials')
    ? 'Incorrect email or password. Please try again.' : message.includes('Email not confirmed')
    ? 'Please check your email and confirm your account.' : message.includes('User already registered')
    ? 'An account with this email already exists. Try signing in.' : message.includes('Password should be at least')
    ? 'Password must be at least 6 characters.'
    : message;

  return (
    <div
      style={{
        padding: '10px 12px',
        backgroundColor: 'rgba(239,68,68,0.08)',
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: '5px',
        color: '#ef4444',
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontWeight: 300,
        marginBottom: '16px',
        lineHeight: 1.5,
      }}
    >
      {friendly}
    </div>
  );
}

function SuccessBanner({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div
      style={{
        padding: '10px 12px',
        backgroundColor: 'rgba(34,197,94,0.08)',
        border: '1px solid rgba(34,197,94,0.3)',
        borderRadius: '5px',
        color: '#16a34a',
        fontFamily: 'Inter, sans-serif',
        fontSize: '13px',
        fontWeight: 300,
        marginBottom: '16px',
        lineHeight: 1.5,
      }}
    >
      {message}
    </div>
  );
}

export default function LoginPage() {
  const router = useRouter();
  const { signIn, signUp } = useAuth();
  const [tab, setTab] = useState<AuthTab>('signin');

  // Sign in state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  // Sign up state
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      // Redirect is handled by AuthContext's loadOrganizations:
      // - No orgs → /onboarding/new
      // - Multiple orgs, no preference → /select-workspace
      // - Single org (onboarding done) → /app
    } catch (err: any) {
      setError(err?.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotSuccess('');
    if (!forgotEmail.trim()) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: `https://notesonthis.com/auth/callback?type=recovery`,
      });
      if (error) throw error;
      setForgotSuccess('Password reset email sent! Check your inbox and follow the link to reset your password.');
    } catch (err: any) {
      setForgotError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');
    setSignupSuccess('');

    if (signupPassword !== signupConfirm) {
      setSignupError('Passwords do not match.');
      return;
    }
    if (signupPassword.length < 6) {
      setSignupError('Password must be at least 6 characters.');
      return;
    }
    if (!signupName.trim()) {
      setSignupError('Please enter your full name.');
      return;
    }

    setSignupLoading(true);
    try {
      const data = await signUp(signupEmail, signupPassword, {
        fullName: signupName.trim(),
        displayName: signupName.trim().split(' ')[0],
        role: 'viewer',
      });

      // If Supabase returned an active session, the user is auto-confirmed — redirect
      if (data?.session) {
        router.push('/onboarding');
        router.refresh();
        return;
      }

      // No session means email confirmation is required
      setSignupSuccess(
        'Account created! Check your email for a confirmation link, then sign in.'
      );
      // Switch to sign-in tab so they can log in after confirming
      setTab('signin');
      setEmail(signupEmail);
    } catch (err: any) {
      setSignupError(err?.message || 'Sign up failed. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const tabBtnStyle = (active: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '10px',
    backgroundColor: active ? 'var(--bg)' : 'transparent',
    border: 'none',
    borderBottom: active ? '2px solid var(--clay)' : '2px solid transparent',
    color: active ? 'var(--ink)' : 'var(--ink-mute)',
    fontFamily: 'JetBrains Mono, monospace',
    fontSize: '10px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.2em',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo + title */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <AppLogo size={40} />
          </div>
          <h1
            style={{
              fontFamily: 'Outfit, sans-serif',
              fontWeight: 200,
              fontSize: '24px',
              color: 'var(--ink)',
              letterSpacing: '-0.01em',
              marginBottom: '6px',
            }}
          >
            Cloud Hidden Ops
          </h1>
          <p
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '10px',
              textTransform: 'uppercase',
              letterSpacing: '0.28em',
              color: 'var(--ink-mute)',
            }}
          >
            Team Operating System
          </p>
        </div>

        {/* Tab switcher */}
        <div
          style={{
            display: 'flex',
            backgroundColor: 'var(--bg-soft)',
            border: '1px solid var(--border-med)',
            borderRadius: '8px 8px 0 0',
            overflow: 'hidden',
          }}
        >
          <button style={tabBtnStyle(tab === 'signin')} onClick={() => { setTab('signin'); setError(''); }}>
            Sign In
          </button>
          <button style={tabBtnStyle(tab === 'signup')} onClick={() => { setTab('signup'); setSignupError(''); setSignupSuccess(''); }}>
            Sign Up
          </button>
        </div>

        {/* Form card */}
        <div
          style={{
            backgroundColor: 'var(--bg-soft)',
            border: '1px solid var(--border-med)',
            borderTop: 'none',
            borderRadius: '0 0 8px 8px',
            padding: '28px 32px 32px',
            marginBottom: '24px',
          }}
        >
          {tab === 'signin' ? (
            showForgot ? (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <p style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, fontSize: '16px', color: 'var(--ink)', marginBottom: '6px' }}>
                    Reset your password
                  </p>
                  <p style={{ fontFamily: 'Inter, sans-serif', fontWeight: 300, fontSize: '13px', color: 'var(--ink-mute)', lineHeight: 1.5 }}>
                    Enter your email and we&apos;ll send you a link to reset your password.
                  </p>
                </div>
                <form onSubmit={handleForgotPassword}>
                  <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Email</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      placeholder="you@cloudhidden.com"
                      style={inputStyle}
                    />
                  </div>
                  {forgotError && <ErrorBanner message={forgotError} />}
                  {forgotSuccess && <SuccessBanner message={forgotSuccess} />}
                  {!forgotSuccess && (
                    <button
                      type="submit"
                      disabled={forgotLoading}
                      style={{
                        width: '100%',
                        padding: '11px 16px',
                        backgroundColor: forgotLoading ? 'var(--border-med)' : 'var(--clay)',
                        border: '1px solid var(--clay)',
                        borderRadius: '5px',
                        color: 'var(--ink)',
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.2em',
                        cursor: forgotLoading ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.15s ease',
                        marginBottom: '12px',
                      }}
                    >
                      {forgotLoading ? 'Sending…' : 'Send Reset Link'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => { setShowForgot(false); setForgotError(''); setForgotSuccess(''); setForgotEmail(''); }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--ink-mute)',
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.2em',
                      cursor: 'pointer',
                      padding: 0,
                      width: '100%',
                      textAlign: 'center' as const,
                    }}
                  >
                    ← Back to Sign In
                  </button>
                </form>
              </div>
            ) : (
            <form onSubmit={handleSignIn}>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@cloudhidden.com"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '8px' }}>
                <label style={labelStyle}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••••••"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '20px', textAlign: 'right' }}>
                <button
                  type="button"
                  onClick={() => { setShowForgot(true); setForgotEmail(email); setError(''); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--ink-mute)',
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '9px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    cursor: 'pointer',
                    padding: 0,
                    textDecoration: 'underline',
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <SuccessBanner message={signupSuccess} />
              <ErrorBanner message={error} />

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '11px 16px',
                  backgroundColor: loading ? 'var(--border-med)' : 'var(--clay)',
                  border: '1px solid var(--clay)',
                  borderRadius: '5px',
                  color: 'var(--ink)',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.2em',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {loading ? 'Signing in…' : 'Sign In'}
              </button>
            </form>
            )
          ) : (
            <form onSubmit={handleSignUp}>
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Full Name</label>
                <input
                  type="text"
                  value={signupName}
                  onChange={(e) => setSignupName(e.target.value)}
                  required
                  placeholder="Your full name"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  required
                  placeholder="you@cloudhidden.com"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Password</label>
                <input
                  type="password"
                  value={signupPassword}
                  onChange={(e) => setSignupPassword(e.target.value)}
                  required
                  placeholder="Min. 6 characters"
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={labelStyle}>Confirm Password</label>
                <input
                  type="password"
                  value={signupConfirm}
                  onChange={(e) => setSignupConfirm(e.target.value)}
                  required
                  placeholder="Repeat password"
                  style={inputStyle}
                />
              </div>

              <ErrorBanner message={signupError} />
              <SuccessBanner message={signupSuccess} />

              <button
                type="submit"
                disabled={signupLoading}
                style={{
                  width: '100%',
                  padding: '11px 16px',
                  backgroundColor: signupLoading ? 'var(--border-med)' : 'var(--clay)',
                  border: '1px solid var(--clay)',
                  borderRadius: '5px',
                  color: 'var(--ink)',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.2em',
                  cursor: signupLoading ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.15s ease',
                }}
              >
                {signupLoading ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          )}
        </div>

        {/* Rate limit notice */}
        <p
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 300,
            fontSize: '11px',
            color: 'var(--ink-mute)',
            textAlign: 'center',
            lineHeight: 1.6,
          }}
        >
          Having trouble? Auth requests are rate-limited. Wait 1 minute between attempts.
        </p>
      </div>
    </div>
  );
}
